import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import {
  AnalysesTypesLowercase,
  KitInProgress,
  Report,
  StatisticsLevel,
} from 'src/app/analyses/interfaces/analyses.types';
import { CategoryReport } from 'src/app/core/interfaces';

@Injectable({
  providedIn: 'root',
})
export class ResultsService {
  analyses: { [id: string]: { results: { [id: string]: any } } } = {};
  report: any = {};
  test: string;

  constructor(private http: HttpClient) {}

  all(type?: string, status?: string): Observable<Report[]> {
    // temporary DNA domain check, until all domains are switched to new APIs
    if (
      type &&
      [
        AnalysesTypesLowercase.DNA as string,
        AnalysesTypesLowercase.Blood as string,
      ].includes(type.toLowerCase())
    ) {
      return this.http.get<Report[]>(`/api/${type.toLowerCase()}/reports`).pipe(
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
    }

    // We need to capitalise tye parameter to fit old api. This is temporary until new api is introduced
    const capitalisedType = type
      ? type.charAt(0).toUpperCase() + type.slice(1)
      : '';
    const params = new HttpParams()
      .append('type', capitalisedType || '')
      .append('status', status);

    return this.http
      .get<Report[]>(`${environment.api}/reports`, { params })
      .pipe(
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
  }

  get(id: string, type?: string): Observable<Report | any> {
    if (type) {
      return this.http
        .get<Report>(`/api/${type.toLocaleLowerCase()}/reports/${id}`)
        .pipe(
          shareReplay({
            bufferSize: 1,
            refCount: true,
          })
        );
    } else {
      // healthscore/questionnaire
      return this.http.get<any>(`${environment.api}/reports/${id}`);
    }
  }

  // Removed from the face of the earth, if re-enabled define interface type
  getPublicQuestionnaire(): Observable<any> {
    return this.http.get<any>(
      `${environment.publicApi}/questionnaire/${environment.products.lifestyleQuestionnaire}`
    );
  }

  save(report: {
    id?: string;
    file?: string;
    name?: string;
    date: string;
    productId?: string;
  }): Observable<any> {
    // Reports api will not be migrated to new api!
    if (report.id) {
      return this.http.put<any>(
        `${environment.api}/reports/${report.id}`,
        report
      );
    }

    return this.http.post<any>(`${environment.api}/reports`, report);
  }

  update(
    id: string,
    report: { id?: string; status?: string; resultId?: string; type?: string }
  ): Observable<any> {
    return this.http.put<any>(`${environment.api}/reports/${id}`, report);
  }

  // Save when report is viewed
  logViewed(productInventoryItemId: string, type: string): Observable<any> {
    if (productInventoryItemId && type) {
      return this.http.post(
        `${environment.api}/resultinteractors/markresultasviewed`,
        {
          productName: 'HI Dashboard',
          productInventoryItemId,
          origin: `HI-${type}`,
        }
      );
    }
    return of();
  }

  statistics(type: string, id = ''): Observable<StatisticsLevel[]> {
    return this.http.get<StatisticsLevel[]>(
      `/api/${type}/statistics/levels${id ? '/' + id : ''}`
    );
  }

  fetchReportsByCategoryId(categoryId: string): Observable<CategoryReport[]> {
    return this.http.get<CategoryReport[]>(`/api/categories/${categoryId}`);
  }

  fetchKitsInProgressByCategoryId(
    categoryId: string
  ): Observable<KitInProgress[]> {
    return this.http.get<KitInProgress[]>(
      `${environment.api}/kits-in-progress`,
      { params: { categoryId } }
    );
  }
}
